import { API_ROUTES } from '@app/common';
import type {
  OAuthFinalizeRequest,
  OAuthInitializeResponse,
  OAuthPendingConnection,
  SocialAccountSummary,
  SocialPlatform,
} from '@app/common';
import { apiFetch } from './index';

export type {
  ConnectTarget,
  OAuthPendingConnection,
  SocialAccountSummary,
  SocialPlatform,
} from '@app/common';

/**
 * Phase 1. Returns where to send the browser.
 *
 * The app then does a full-page navigation to `authorizeUrl` — not a popup and
 * not fetch. The provider's consent screen sets cookies and refuses to render in
 * a frame, and a popup gets blocked whenever the click is one async hop away
 * from the user's gesture, which this one is.
 */
export const initializeConnect = (
  platform: SocialPlatform,
  input: { brandId: string; redirectTo?: string }
): Promise<OAuthInitializeResponse> =>
  apiFetch<OAuthInitializeResponse>(API_ROUTES.OAUTH_INITIALIZE(platform), {
    method: 'POST',
    body: JSON.stringify(input),
  });

/**
 * Phase 3a. Reads what the callback discovered.
 *
 * There is no client call for phase 2 — the provider redirects the browser to
 * the backend, which redirects back here with `?connection=<id>`. That id is
 * this function's argument.
 */
export const getPendingConnection = (connectionId: string): Promise<OAuthPendingConnection> =>
  apiFetch<OAuthPendingConnection>(API_ROUTES.OAUTH_CONNECTION(connectionId));

/** Phase 3b. Connects the chosen targets; everything unpicked is discarded with the token. */
export const finalizeConnect = (
  connectionId: string,
  input: OAuthFinalizeRequest
): Promise<SocialAccountSummary[]> =>
  apiFetch<SocialAccountSummary[]>(API_ROUTES.OAUTH_CONNECTION(connectionId), {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const listSocialAccounts = (brandId?: string): Promise<SocialAccountSummary[]> =>
  apiFetch<SocialAccountSummary[]>(
    brandId ? `${API_ROUTES.SOCIAL_ACCOUNTS}?brandId=${brandId}` : API_ROUTES.SOCIAL_ACCOUNTS
  );

export const disconnectSocialAccount = (accountId: string): Promise<{ disconnected: true }> =>
  apiFetch<{ disconnected: true }>(API_ROUTES.SOCIAL_DISCONNECT(accountId), { method: 'POST' });
